import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, AlertTriangle, CheckCircle2, Clock, Loader2, Eye, Users
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const statusConfig = {
  overdue: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200", icon: AlertTriangle },
  completed: { color: "text-green-700", bg: "bg-green-50", border: "border-green-200", icon: CheckCircle2 },
  pending: { color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", icon: Clock },
  in_progress: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", icon: Clock },
};

const ManagerTeamCompliancePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/manager/team-compliance").then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout title="Team Compliance" variant="manager">
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  const s = data?.summary || {};
  const items = data?.compliance_items || [];
  const filtered = tab === "all" ? items : items.filter(c => c.status === tab);

  return (
    <AdminLayout title="Team Compliance" variant="manager">
      <motion.div className="space-y-6" variants={container} initial="hidden" animate="show" data-testid="team-compliance-page">
        <motion.div variants={item}>
          <h1 className="text-2xl font-extrabold tracking-tight">Team Compliance</h1>
          <p className="text-sm text-muted-foreground mt-1">Monitor compliance status across your direct reports</p>
        </motion.div>

        {/* Summary */}
        <motion.div variants={item} className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Total Items", value: s.total, icon: Shield, color: "#3b82f6" },
            { label: "Completed", value: s.completed, icon: CheckCircle2, color: "#10b981" },
            { label: "Pending", value: s.pending, icon: Clock, color: "#f59e0b" },
            { label: "Overdue", value: s.overdue, icon: AlertTriangle, color: s.overdue > 0 ? "#ef4444" : "#10b981" },
            { label: "Rate", value: `${s.compliance_rate || 0}%`, icon: Shield, color: s.compliance_rate >= 90 ? "#10b981" : "#ef4444" },
          ].map((st, i) => (
            <Card key={i} className="border shadow-none"><CardContent className="p-3.5">
              <div className="flex items-center gap-2 mb-1"><div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: st.color + "14" }}><st.icon className="w-3.5 h-3.5" style={{ color: st.color }} /></div></div>
              <p className="text-lg font-extrabold" style={{ color: st.color }}>{st.value}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{st.label}</p>
            </CardContent></Card>
          ))}
        </motion.div>

        {/* Filter tabs */}
        <motion.div variants={item}>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-muted/50 h-9">
              <TabsTrigger value="all" className="text-xs" data-testid="tab-all">All ({items.length})</TabsTrigger>
              <TabsTrigger value="overdue" className="text-xs" data-testid="tab-overdue">Overdue ({items.filter(c => c.status === "overdue").length})</TabsTrigger>
              <TabsTrigger value="pending" className="text-xs" data-testid="tab-pending">Pending</TabsTrigger>
              <TabsTrigger value="completed" className="text-xs" data-testid="tab-completed">Completed</TabsTrigger>
            </TabsList>
          </Tabs>
        </motion.div>

        {/* Compliance Items */}
        {filtered.length === 0 ? (
          <motion.div variants={item}>
            <Card className="border shadow-none"><CardContent className="p-8 text-center text-muted-foreground"><Shield className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No compliance items in this category</p></CardContent></Card>
          </motion.div>
        ) : (
          <motion.div variants={item} className="space-y-2">
            {filtered.map((c, i) => {
              const sc = statusConfig[c.status] || statusConfig.pending;
              const SIcon = sc.icon;
              return (
                <Card key={i} className={cn("border shadow-none border-l-4", sc.border)} data-testid={`compliance-item-${i}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", sc.bg)}><SIcon className={cn("w-4 h-4", sc.color)} /></div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-bold">{c.user_name}</h3>
                          {c.is_mandatory && <Badge className="bg-red-50 text-red-600 border-red-200 text-[10px]">Required</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">{c.template_title} &middot; Due: {c.due_date || "N/A"}</p>
                        {c.department && <p className="text-[10px] text-muted-foreground">{c.department}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={cn("text-[10px] border", sc.bg, sc.color, sc.border)}>{c.status}</Badge>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(`/manager/team/${c.user_id}`)} data-testid={`view-${i}`}><Eye className="w-3.5 h-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </motion.div>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default ManagerTeamCompliancePage;
